const PAYMENT_CONTEXT_KEYS = [
  'pendingOrderId',
  'pendingPayChannel',
  'pendingAppAccountToken',
  'pendingPaymentOwnerUserId',
] as const

const APPLE_PAYMENT_CONTEXT_KEY = 'pendingApplePaymentContextV2'

export type PendingApplePaymentPhase =
  | 'order_created'
  | 'storekit_started'
  | 'storekit_verified'
  | 'server_confirming'

export type PendingApplePaymentContext = {
  version: 2
  ownerUserId: string
  orderId: string
  packageId: string
  productId: string
  appAccountToken: string
  transactionId?: string
  phase: PendingApplePaymentPhase
  updatedAt: string
}

export function savePendingApplePaymentContext(
  context: Omit<PendingApplePaymentContext, 'version' | 'updatedAt'>,
): PendingApplePaymentContext {
  const stored: PendingApplePaymentContext = {
    ...context,
    version: 2,
    updatedAt: new Date().toISOString(),
  }
  localStorage.setItem(APPLE_PAYMENT_CONTEXT_KEY, JSON.stringify(stored))
  localStorage.setItem('pendingOrderId', stored.orderId)
  localStorage.setItem('pendingPayChannel', 'apple_iap')
  localStorage.setItem('pendingAppAccountToken', stored.appAccountToken)
  localStorage.setItem('pendingPaymentOwnerUserId', stored.ownerUserId)
  return stored
}

export function loadPendingApplePaymentContext(): PendingApplePaymentContext | null {
  const raw = localStorage.getItem(APPLE_PAYMENT_CONTEXT_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<PendingApplePaymentContext>
    if (
      parsed.version !== 2
      || !parsed.ownerUserId
      || !parsed.orderId
      || !parsed.packageId
      || !parsed.productId
      || !parsed.appAccountToken
      || !parsed.phase
      || !parsed.updatedAt
    ) return null
    return parsed as PendingApplePaymentContext
  } catch {
    return null
  }
}

export function clearPendingApplePaymentContext(orderId?: string): void {
  const current = loadPendingApplePaymentContext()
  if (orderId && current?.orderId !== orderId) return
  localStorage.removeItem(APPLE_PAYMENT_CONTEXT_KEY)
  for (const key of PAYMENT_CONTEXT_KEYS) localStorage.removeItem(key)
}

export function clearPendingPaymentContext(): void {
  localStorage.removeItem(APPLE_PAYMENT_CONTEXT_KEY)
  for (const key of PAYMENT_CONTEXT_KEYS) localStorage.removeItem(key)
}

export function bindPendingPaymentContextToUser(userId: string): void {
  localStorage.setItem('pendingPaymentOwnerUserId', userId)
}

export function pendingPaymentBelongsTo(userId: string): boolean {
  const owner = localStorage.getItem('pendingPaymentOwnerUserId')
  return owner === null || owner === userId
}
